import type { AiSlotDuplicates } from "../registry/schema.js";
import { SLOT_RESOLUTION_REASON, slotNone, slotResolved, type SlotResolution } from "./slot-resolution.js";
import { isAddMembersDescription, isUnaccentedText } from "./name-text.js";
import { sameListedWord, wordsOf, type Word } from "./vi-words.js";

/**
 * US-AIV2-0022 — ô `text` / `list`: CHỮ NGUYÊN VĂN (schema Sổ: *"text/list → chữ nguyên văn"*). Không chuẩn hoá,
 * không sửa chính tả — thứ người dùng gõ là thứ vào tên nhóm/tên đợt. Chỉ cắt khoảng trắng hai đầu.
 */
export function resolveTextSlot(text: string | null | undefined): SlotResolution<string> {
  const raw = (text ?? "").trim();
  if (raw === "") return slotNone(SLOT_RESOLUTION_REASON.MISSING);
  return slotResolved(raw, raw);
}

/**
 * 🔴 UX-SMALL-WORDING (V-5 · @UC0197) — ô LỜI NHẮN (Sổ khai `textForm: "note"`: ghi chú gửi thủ quỹ · lý do từ chối). Nghiệm thu 16/10: "ck 200k quỹ tết
 * rồi nha" ⇒ phiếu «Ghi chú gửi thủ quỹ: quỹ tết rồi nha» — Phiếu gạch cả LỜI ĐỆM cuối câu vào ô. Bỏ đúng một đuôi: [rồi] + ≥1 trợ từ cuối câu + [một xưng
 * hô gọi] (tập ĐÓNG dưới — "rồi"/xưng hô chỉ bỏ khi đi cùng trợ từ: "bố chuyển rồi" giữ "rồi"). Phần còn lại giữ NGUYÊN VĂN (như `resolveTextSlot`). So
 * chữ bằng `sameListedWord`: gõ không dấu "nha" là trợ từ, "nhà" có dấu thì không ("tiền nhà" giữ). Còn rỗng ⇒ `missing` (ô tuỳ chọn bỏ trống).
 */
const NOTE_PARTICLES: readonly Word[] = wordsOf("nhé nha nhe nhá nhen nghen ạ nè hen");
const NOTE_DONE: readonly Word[] = wordsOf("rồi");
const NOTE_VOCATIVES: readonly Word[] = wordsOf("anh chị em cô chú bác ơi");

export function resolveNoteText(text: string | null | undefined): SlotResolution<string> {
  const original = (text ?? "").trim();
  const raw = original.normalize("NFC");
  const w = wordsOf(raw);
  // review V-5 MEDIUM-1: (a) chữ VIẾT HOA là tên ("tổ A", "chị Nha") — không bao giờ là lời đệm; (b) chữ gõ không dấu chỉ khớp một từ CÓ dấu của tập ("roi"≡"rồi",
  // "nhe"≡"nhé") khi CẢ ghi chú gõ không dấu; khớp đúng mặt chữ ("nha", "nhe" viết đúng chính tả) luôn được; (c) "ạ" chỉ khi gõ đúng "ạ" ("a" = tổ a, lớp a).
  const allPlain = isUnaccentedText(raw);
  const matches = (x: Word, listed: Word): boolean => {
    if (x.accented === listed.accented) return true;
    if (listed.key === "a") return false;
    return sameListedWord(x, listed) && (!x.plain || listed.plain || allPlain);
  };
  const is = (i: number, set: readonly Word[]): boolean => {
    const x = w[i];
    if (x === undefined || /^\p{Lu}/u.test(raw.slice(x.start, x.end))) return false;
    return set.some((listed) => matches(x, listed));
  };
  let end = w.length;
  if (is(end - 1, NOTE_VOCATIVES) && is(end - 2, NOTE_PARTICLES)) end -= 1;
  const lastParticle = end;
  while (is(end - 1, NOTE_PARTICLES)) end -= 1;
  if (end < lastParticle && is(end - 1, NOTE_DONE)) end -= 1;
  if (end === w.length) return resolveTextSlot(original);
  const cut = end === 0 ? "" : raw.slice(0, w[end - 1]!.end).trim();
  return resolveTextSlot(cut.replace(/[\s,.;!…-]+$/u, ""));
}

/**
 * Phân tách danh sách người gõ: phẩy · chấm phẩy · xuống dòng · `&` · " và "/" va " — bỏ rỗng, giữ thứ tự, bỏ trùng.
 * 🔴 KHÔNG lượng từ lồng nhau (`\s*(…\s+…)\s*`): review #815 H2 đo bản cũ backtracking ~n³ — 1.500 khoảng trắng = 2,3 s,
 * 5.000 ≈ 84 s, chặn event loop cả API (dán CSV/Excel là thao tác thường của ô `list`). Bản này tuyến tính (100.000 khoảng
 * trắng ≈ 0,3 ms); mảnh thừa khoảng trắng được `trim()` sau khi tách. Cap độ dài `text` ở cửa HTTP là việc 0023 (zod `.max`).
 */
const LIST_SEPARATOR_RE = /[,;\n&]|\s(?:và|va)\s/u;

/**
 * Khoá so trùng của chế độ `ask` — CỐ Ý RỘNG HƠN phép so của `drop`: gấp hoa-thường (vi) và gộp khoảng trắng thừa
 * ("Lê  Minh" ≡ "Lê Minh"), nhưng **KHÔNG gấp dấu tiếng Việt** — "Minh" và "Mính" là hai cái tên, hỏi ở đó là làm phiền.
 *
 * Bất đối xứng với `drop` là quyết định, không phải sót: `drop` phải giữ **bit-for-bit** hành vi cũ (0 regress), còn `ask`
 * là lưới an toàn nên đứng về phía HỎI. Hỏi nhầm một lần ⇒ người dùng gõ lại; gộp nhầm một lần ⇒ mất một người và một
 * nghĩa vụ tiền, **lặng lẽ**.
 */
/* Re-review vòng 2 #1106 L-1: export — `withCreateGroupNames` (api) khử trùng tên GIỮA các câu bằng chính khoá này (một nguồn, không phép so tên thứ hai). */
export function duplicateKey(item: string): string {
  return item.replace(/\s+/gu, " ").toLocaleLowerCase("vi");
}

/**
 * Tách chữ thành danh sách mục. `duplicates`:
 *  · `"drop"` (MẶC ĐỊNH — mọi ô không khai gì) — gộp mục trùng nguyên văn, giữ thứ tự. Hành vi từ 0022, không đổi.
 *  · `"ask"` — có mục lặp ⇒ `none/duplicate_items` kèm `token` = mục bị lặp (để câu hỏi lại có trọng tâm). **0 giá trị
 *    rời hàm** ⇒ tầng trên không dựng được phiếu ⇒ không ghi gì cho tới khi người dùng trả lời (US-AIV2-0033 @UC0194).
 */
/**
 * 🔴 US-AIV2-0033 vòng 2 (review #842 HIGH) — SỐ MỤC mà một chuỗi cho ra, dùng để phát hiện **trích xuất MỘT PHẦN**.
 *
 * Bối cảnh, vì con số này canh TIỀN: ô `list` để **span của LLM** quyết danh sách, câu nguyên văn chỉ dùng khi LLM
 * không gạch chân. Trần span là **400 ký tự**, mà **30 tên Việt đầy đủ ≈ 405 ký tự** ⇒ lớp 30 cháu nằm NGAY TRÊN
 * ranh giới, lớp 35–40 rơi qua chắc chắn. Khi đó span chỉ phủ ~20 tên ⇒ thẻ nói "Thêm 20 người" ⇒ ghi 20 suất +
 * 20 `member_dues`: **mười cháu biến mất cùng mười nghĩa vụ đóng, im lặng**. Phép đối chiếu facts KHÔNG bắt được
 * vì `preview` và `execute` cùng đọc một nguồn đã bị cắt.
 *
 * Nên tầng trên so `countListItems(nguyên văn)` với `countListItems(span)`: span cho ÍT mục hơn ⇒ **đã mất người** ⇒
 * hỏi lại, KHÔNG ghi. Cùng nguyên tắc `duplicates: "ask"`: *thà hỏi một câu còn hơn ghi sai một đồng.*
 */
export function countListItems(text: string | null | undefined): number {
  const r = resolveListSlot(text);
  return r.status === "resolved" ? r.value.length : 0;
}

/**
 * Mục của một danh sách gõ tay — CÙNG dấu tách với ô `list` (`LIST_SEPARATOR_RE`), bỏ rỗng, GIỮ trùng và thứ tự. US-AIV2-0113 M01: lưới đọc danh sách
 * người trong câu tạo nhóm (`memberListFromSentence`) tách bằng chính hàm này, để "tên" ở đó và ở ô `danhSach` là một luật.
 */
export function listItemsOf(text: string | null | undefined): string[] {
  return (text ?? "")
    .trim()
    .split(LIST_SEPARATOR_RE)
    .map((part) => part.trim())
    .filter((item) => item !== "");
}

export function resolveListSlot(text: string | null | undefined, duplicates: AiSlotDuplicates = "drop"): SlotResolution<readonly string[]> {
  const raw = (text ?? "").trim();
  if (raw === "") return slotNone(SLOT_RESOLUTION_REASON.MISSING);
  const seen = new Set<string>();
  const items: string[] = [];
  const unaccented = isUnaccentedText(raw);
  for (const item of listItemsOf(raw)) {
    // 🔴 @UC0393: mục là CÂU LỆNH thêm người ("thêm thành viên vào nhóm"), không phải một người ⇒ bỏ; còn 0 người ⇒ none/missing (hỏi lại).
    if (isAddMembersDescription(item, unaccented)) continue;
    if (duplicates === "ask") {
      const key = duplicateKey(item);
      // Rỗng ⇒ `none/missing` (vế dưới) phải được xét TRƯỚC: danh sách toàn dấu tách không phải "có mục lặp".
      if (seen.has(key)) return slotNone(SLOT_RESOLUTION_REASON.DUPLICATE_ITEMS, item);
      seen.add(key);
      items.push(item);
      continue;
    }
    if (seen.has(item)) continue;
    seen.add(item);
    items.push(item);
  }
  if (items.length === 0) return slotNone(SLOT_RESOLUTION_REASON.MISSING);
  return slotResolved(Object.freeze(items), items.join(", "));
}
